/**
 * UWGlobalDSCRTab.jsx
 * Replicates the "Global DSCR" tab showing:
 * - Global Cash Available (Personal + Business + Affiliate)
 * - Global Debt Service breakdown
 * - Global Cash After Debt Service
 * - Global DSCR / Stress Test DSCR
 * - Liquidity & Debt Calculations panel (right side)
 */
import React, { useState, useMemo } from "react";
import { cn, formatCurrency, formatMultiple, formatPercent, calcDSCR, dscrColor, dscrBadge, dscrLabel } from "../../lib/utils";

const PERIODS = [
  { label: "Historical (2022)", months: 12 },
  { label: "Historical (2023)", months: 12 },
  { label: "Historical (2024)", months: 12 },
  { label: "7/31/2025",         months: 7  },
  { label: "Proj Borrower Y1",  months: 12 },
  { label: "Proj Bank Stressed Y1", months: 12 },
  { label: "Proj Borrower Y2",  months: 12 },
  { label: "Proj Bank Stressed Y2", months: 12 },
];

const EMPTY_PERIOD_DATA = {
  personalCashAvailable:  0,
  businessCashAvailable:  0,
  affiliateCashAvailable: 0,
  personalDebtService:    0,
  businessDebtService:    0,
  affiliateDebtService:   0,
};

const DEFAULT_LIQUIDITY = {
  personalIncludingEquity: 0,
  borrower: 0,
  affiliates: 0,
  requiredEquityInjection: 0,
  lessOther: 0,
  equityInjectionAlreadyPaid: 0,
  workingCapital: 0,
  plus_other: 0,
};

const DEFAULT_DEBT_GLOBAL = [
  { entity: "Personal",   currentBalance: 1041792, annualPayments: 374490, monthlyPayments: 31208 },
  { entity: "Business*",  currentBalance: 663792,  annualPayments: 166008, monthlyPayments: 13834 },
  { entity: "Affiliates*",currentBalance: 0,       annualPayments: 0,      monthlyPayments: 0 },
];

function NumInput({ value, onChange, prefix = "$" }) {
  return (
    <input
      type="number"
      value={value === 0 ? "" : value}
      onChange={e => onChange(Number(e.target.value) || 0)}
      placeholder="0"
      className="w-full text-right font-mono text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5
                 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white tabular-nums"
    />
  );
}

function StaticNum({ value, className = "" }) {
  const num = Number(value) || 0;
  return (
    <span className={cn("font-mono text-xs tabular-nums", num < 0 ? "text-red-600" : "text-gray-800", className)}>
      {num !== 0 ? formatCurrency(num) : "—"}
    </span>
  );
}

export default function UWGlobalDSCRTab({ initialData, borrowerName = "0", onDataChange }) {
  const [periods, setPeriods] = useState(
    initialData?.periods || PERIODS.map(() => ({ ...EMPTY_PERIOD_DATA }))
  );
  const [liquidity, setLiquidity]     = useState(initialData?.liquidity || DEFAULT_LIQUIDITY);
  const [debtGlobal, setDebtGlobal]   = useState(initialData?.debtGlobal || DEFAULT_DEBT_GLOBAL);
  const [useLastFYEPersonal, setUseLastFYEPersonal] = useState(false);
  const [useLastFYEAffiliate, setUseLastFYEAffiliate] = useState(false);

  const updatePeriod = (idx, key, value) => {
    setPeriods(prev => {
      const next = prev.map((p, i) => i === idx ? { ...p, [key]: value } : p);
      onDataChange?.({ periods: next });
      return next;
    });
  };

  // Derived totals per period
  const totals = useMemo(() => periods.map(p => {
    const globalCash = (Number(p.personalCashAvailable) || 0)
                     + (Number(p.businessCashAvailable) || 0)
                     + (Number(p.affiliateCashAvailable) || 0);
    const globalDS   = (Number(p.personalDebtService) || 0)
                     + (Number(p.businessDebtService) || 0)
                     + (Number(p.affiliateDebtService) || 0);
    const afterDS    = globalCash - globalDS;
    const dscr       = calcDSCR(globalCash, globalDS);
    const stressDSCR = calcDSCR(globalCash, globalDS * 1.05);
    return { globalCash, globalDS, afterDS, dscr, stressDSCR,
      personal:  { cf: p.personalCashAvailable,  ds: p.personalDebtService,  after: p.personalCashAvailable  - p.personalDebtService  },
      business:  { cf: p.businessCashAvailable,  ds: p.businessDebtService,  after: p.businessCashAvailable  - p.businessDebtService  },
      affiliate: { cf: p.affiliateCashAvailable, ds: p.affiliateDebtService, after: p.affiliateCashAvailable - p.affiliateDebtService },
    };
  }), [periods]);

  // Liquidity totals
  const preCLosing  = (Number(liquidity.personalIncludingEquity) + Number(liquidity.borrower) + Number(liquidity.affiliates)) || 0;
  const postClosing = preCLosing - (Number(liquidity.requiredEquityInjection) + Number(liquidity.lessOther)) 
                    + Number(liquidity.equityInjectionAlreadyPaid) + Number(liquidity.workingCapital) + Number(liquidity.plus_other);

  // Debt global totals
  const debtTotals = {
    balance: debtGlobal.reduce((s, d) => s + (Number(d.currentBalance) || 0), 0),
    annual:  debtGlobal.reduce((s, d) => s + (Number(d.annualPayments) || 0), 0),
    monthly: debtGlobal.reduce((s, d) => s + (Number(d.monthlyPayments) || 0), 0),
  };

  const TH = "bg-[#1e3a5f] text-white text-[10px] font-semibold px-2 py-1.5 text-center border-r border-gray-600";
  const TD_LABEL = "px-3 py-1 text-xs text-gray-700 border-r border-gray-200 font-medium whitespace-nowrap bg-gray-50 min-w-[180px]";

  function CashRow({ label, rowKey, bgClass = "" }) {
    return (
      <tr className={cn("border-b border-gray-100", bgClass)}>
        <td className={TD_LABEL}>{label}</td>
        {periods.map((p, i) => (
          <td key={i} className="px-1 py-0.5 border-r border-gray-200 min-w-[100px]">
            <NumInput value={p[rowKey]} onChange={v => updatePeriod(i, rowKey, v)} />
          </td>
        ))}
      </tr>
    );
  }

  function TotalRow({ label, values, className = "" }) {
    return (
      <tr className={cn("border-t-2 border-[#1e3a5f]", className)}>
        <td className="px-3 py-1.5 text-xs font-bold bg-[#1e3a5f] text-white sticky left-0 border-r border-gray-600 min-w-[180px]">
          {label}
        </td>
        {values.map((v, i) => {
          const num = Number(v) || 0;
          return (
            <td key={i} className={cn(
              "px-2 py-1.5 text-right font-mono text-xs font-bold tabular-nums border-r border-gray-300 min-w-[100px]",
              num < 0 ? "text-red-300" : "text-white",
              "bg-[#1e3a5f]"
            )}>
              {num !== 0 ? formatCurrency(num) : ""}
            </td>
          );
        })}
      </tr>
    );
  }

  function DSCRRow({ label, dscrValues }) {
    return (
      <tr className="border-t-2 border-[#1e3a5f] bg-[#f0f7ff]">
        <td className="px-3 py-1.5 text-xs font-bold bg-[#dce6f1] text-[#1e3a5f] sticky left-0 border-r border-gray-300 min-w-[180px]">
          {label}
        </td>
        {dscrValues.map((d, i) => (
          <td key={i} className="px-2 py-1.5 text-center border-r border-gray-300 min-w-[100px]">
            {d !== null ? (
              <span className={cn("font-bold text-sm font-mono", dscrColor(d))}>
                {formatMultiple(d)}
              </span>
            ) : <span className="text-gray-400 text-xs">N/A</span>}
          </td>
        ))}
      </tr>
    );
  }

  return (
    <div className="h-full overflow-auto bg-white">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-2 bg-[#dce6f1] border-b border-gray-300 sticky top-0 z-20">
        <span className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Global Debt Service Coverage Ratios</span>
      </div>

      <div className="flex gap-6 p-4">
        {/* ── Left: DSCR Table ── */}
        <div className="flex-1 overflow-x-auto">
          <table className="border-collapse w-full text-xs">
            <thead>
              <tr className="bg-[#1e3a5f]">
                <th className="px-3 py-1.5 text-left text-[10px] text-gray-300 min-w-[180px] border-r border-gray-600">Period</th>
                {PERIODS.map((p, i) => (
                  <th key={i} className={TH} style={{ minWidth: 100 }}>
                    <div>{p.label}</div>
                    <div className="text-[9px] opacity-70 mt-0.5"># Months: {p.months}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ── Personal ── */}
              <tr className="bg-[#eef4fb]"><td colSpan={9} className="px-3 py-1 text-[10px] font-bold text-[#1e3a5f] uppercase tracking-wide">Personal (Guarantor)</td></tr>
              <CashRow label="Personal Cash Available"  rowKey="personalCashAvailable"  />
              <CashRow label="Personal Debt Service"    rowKey="personalDebtService"     />
              <tr className="border-b border-gray-200">
                <td className={TD_LABEL + " font-semibold"}>Personal Cash After Debt Service</td>
                {totals.map((t, i) => <td key={i} className={cn("px-2 py-1 text-right font-mono text-xs tabular-nums border-r border-gray-200", t.personal.after < 0 ? "text-red-600" : "text-gray-800")}>{t.personal.after !== 0 ? formatCurrency(t.personal.after) : "—"}</td>)}
              </tr>

              {/* ── Business ── */}
              <tr className="bg-[#eef4fb]"><td colSpan={9} className="px-3 py-1 text-[10px] font-bold text-[#1e3a5f] uppercase tracking-wide">Business</td></tr>
              <CashRow label="Business Cash Available"  rowKey="businessCashAvailable"  />
              <CashRow label="Business Debt Service"    rowKey="businessDebtService"    />
              <tr className="border-b border-gray-200">
                <td className={TD_LABEL + " font-semibold"}>Business Cash After Debt Service</td>
                {totals.map((t, i) => <td key={i} className={cn("px-2 py-1 text-right font-mono text-xs tabular-nums border-r border-gray-200", t.business.after < 0 ? "text-red-600" : "text-gray-800")}>{t.business.after !== 0 ? formatCurrency(t.business.after) : "—"}</td>)}
              </tr>

              {/* ── Affiliate ── */}
              <tr className="bg-[#eef4fb]"><td colSpan={9} className="px-3 py-1 text-[10px] font-bold text-[#1e3a5f] uppercase tracking-wide">Affiliate</td></tr>
              <CashRow label="Affiliate Cash Available" rowKey="affiliateCashAvailable" />
              <CashRow label="Affiliate Debt Service"   rowKey="affiliateDebtService"   />
              <tr className="border-b border-gray-200">
                <td className={TD_LABEL + " font-semibold"}>Affiliate Cash After Debt Service</td>
                {totals.map((t, i) => <td key={i} className={cn("px-2 py-1 text-right font-mono text-xs tabular-nums border-r border-gray-200", t.affiliate.after < 0 ? "text-red-600" : "text-gray-800")}>{t.affiliate.after !== 0 ? formatCurrency(t.affiliate.after) : "—"}</td>)}
              </tr>

              {/* ── Global Totals ── */}
              <TotalRow label="Global Cash Available"         values={totals.map(t => t.globalCash)} />
              <TotalRow label="Global Debt Service"           values={totals.map(t => t.globalDS)} />
              <tr className="bg-gray-50 border-b border-gray-200">
                <td className="px-3 py-1.5 text-xs font-bold bg-[#dce6f1] text-[#1e3a5f] sticky left-0 border-r border-gray-300 min-w-[180px]">
                  Global Cash After Debt Service
                </td>
                {totals.map((t, i) => (
                  <td key={i} className={cn(
                    "px-2 py-1.5 text-right font-mono text-xs font-bold tabular-nums border-r border-gray-300 min-w-[100px]",
                    t.afterDS < 0 ? "text-red-600" : "text-green-700"
                  )}>
                    {t.globalDS !== 0 ? formatCurrency(t.afterDS) : ""}
                  </td>
                ))}
              </tr>

              {/* ── DSCR rows ── */}
              <DSCRRow label="Global DSCR"                 dscrValues={totals.map(t => t.dscr)} />
              <tr className="bg-[#fff8e1] border-b border-gray-200">
                <td className="px-3 py-1.5 text-xs font-bold text-amber-800 border-r border-gray-300 min-w-[180px]">
                  (Stress Test +2.00%) Global DSCR
                </td>
                {totals.map((t, i) => (
                  <td key={i} className="px-2 py-1.5 text-center border-r border-gray-300 min-w-[100px]">
                    {t.stressDSCR !== null ? (
                      <span className={cn("font-bold text-sm font-mono", dscrColor(t.stressDSCR))}>
                        {formatMultiple(t.stressDSCR)}
                      </span>
                    ) : <span className="text-gray-400 text-xs">N/A</span>}
                  </td>
                ))}
              </tr>

              {/* ── Toggles ── */}
              <tr className="border-t border-gray-200">
                <td colSpan={9} className="px-3 py-2">
                  <div className="flex gap-6 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Use Last FYE Personal Cash Flow in Projected?</span>
                      <button onClick={() => setUseLastFYEPersonal(v => !v)}
                        className={cn("px-3 py-0.5 rounded border text-xs font-bold",
                          useLastFYEPersonal ? "bg-[#1e3a5f] text-white" : "bg-white text-gray-700 border-gray-300"
                        )}>
                        {useLastFYEPersonal ? "Yes" : "No"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Use Last FYE Affiliate Cash Flow in Projected?</span>
                      <button onClick={() => setUseLastFYEAffiliate(v => !v)}
                        className={cn("px-3 py-0.5 rounded border text-xs font-bold",
                          useLastFYEAffiliate ? "bg-[#1e3a5f] text-white" : "bg-white text-gray-700 border-gray-300"
                        )}>
                        {useLastFYEAffiliate ? "Yes" : "No"}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Right: Liquidity & Debt Panel ── */}
        <div className="w-80 shrink-0 space-y-4">
          {/* Liquidity */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2 uppercase tracking-wide">
              Liquidity & Debt Calculations
            </div>
            <div className="p-3 space-y-1 text-xs">
              <div className="flex justify-between font-semibold text-gray-500 border-b pb-1 mb-1">
                <span>Liquidity (Global)</span><span>Amount</span>
              </div>
              {[
                ["Personal (Incl. Equity Injection – Already Paid)", "personalIncludingEquity"],
                ["Borrower", "borrower"],
                ["Affiliates", "affiliates"],
              ].map(([label, key]) => (
                <div key={key} className="flex justify-between items-center gap-2 py-0.5">
                  <span className="text-gray-600 flex-1">{label}</span>
                  <input
                    type="number"
                    value={liquidity[key] || ""}
                    onChange={e => setLiquidity(prev => ({ ...prev, [key]: Number(e.target.value) || 0 }))}
                    className="w-24 text-right font-mono text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="0"
                  />
                </div>
              ))}
              <div className="flex justify-between font-bold text-[#1e3a5f] border-t pt-1 mt-1">
                <span>Total Pre-Closing Liquidity</span>
                <span className="font-mono">{formatCurrency(preCLosing)}</span>
              </div>
              <div className="border-t pt-1 mt-2 space-y-1">
                {[
                  ["Less: Required Equity Injection", "requiredEquityInjection", true],
                  ["Less: Other", "lessOther", true],
                  ["Plus: Equity Injection – Already Paid", "equityInjectionAlreadyPaid", false],
                  ["Plus: Working Capital", "workingCapital", false],
                  ["Plus: Other", "plus_other", false],
                ].map(([label, key, isLess]) => (
                  <div key={key} className="flex justify-between items-center gap-2 py-0.5">
                    <span className={cn("flex-1", isLess ? "text-red-600" : "text-green-700")}>{label}</span>
                    <input
                      type="number"
                      value={liquidity[key] || ""}
                      onChange={e => setLiquidity(prev => ({ ...prev, [key]: Number(e.target.value) || 0 }))}
                      className="w-24 text-right font-mono text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-bold text-[#1e3a5f] border-t pt-1 mt-1">
                <span>Total Post-Closing Liquidity</span>
                <span className={cn("font-mono", postClosing < 0 ? "text-red-600" : "text-green-700")}>
                  {formatCurrency(postClosing)}
                </span>
              </div>
            </div>
          </div>

          {/* Debt Summary */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-[#1e3a5f] text-white text-xs font-bold px-3 py-2 uppercase tracking-wide">
              Debt (Global)
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#dce6f1] text-[#1e3a5f]">
                  <th className="px-3 py-1.5 text-left font-semibold">Entity</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Annual</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Monthly</th>
                </tr>
              </thead>
              <tbody>
                {debtGlobal.map((d, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-1 text-gray-700">{d.entity}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums text-gray-800">{formatCurrency(d.currentBalance)}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums text-gray-800">{formatCurrency(d.annualPayments)}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums text-gray-800">{formatCurrency(d.monthlyPayments)}</td>
                  </tr>
                ))}
                <tr className="bg-[#1e3a5f] text-white font-bold">
                  <td className="px-3 py-1.5">Totals</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatCurrency(debtTotals.balance)}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatCurrency(debtTotals.annual)}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatCurrency(debtTotals.monthly)}</td>
                </tr>
              </tbody>
            </table>
            <div className="px-3 py-1.5 text-[10px] text-gray-400 italic border-t border-gray-200">
              * See Debt Schedule if applicable for additional data
            </div>
          </div>

          {/* Months of Liquidity */}
          <div className="border border-gray-300 rounded-lg p-3">
            <div className="text-xs font-bold text-[#1e3a5f] uppercase mb-2">Months of Liquidity (Post-Closing)</div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Months Available:</span>
              <span className={cn("font-mono text-sm font-bold",
                (postClosing / (debtTotals.monthly || 1)) < 3 ? "text-red-600" :
                (postClosing / (debtTotals.monthly || 1)) < 6 ? "text-amber-600" : "text-green-700"
              )}>
                {debtTotals.monthly > 0 ? (postClosing / debtTotals.monthly).toFixed(1) : "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
        <div className="flex items-center gap-1.5"><span className="dscr-pass">PASS</span><span>≥ 1.25x</span></div>
        <div className="flex items-center gap-1.5"><span className="dscr-warn">MARGINAL</span><span>1.00x – 1.24x</span></div>
        <div className="flex items-center gap-1.5"><span className="dscr-fail">FAIL</span><span>&lt; 1.00x</span></div>
        <span className="ml-auto text-blue-600 font-medium">Click any input cell to update</span>
      </div>
    </div>
  );
}
